import { TrendingUp, Users, Target, Clock, Award, Globe, Heart, Shield, BookOpen, ChevronRight } from 'lucide-react';
import ProgramDetail from '@/components/ProgramDetail';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Economic = () => {
  const { t } = useLanguage();
  
  const economicDetails = [
    t('programs.economic.details.background', 'The Economic Empowerment program was established in 2012 as the fourth branch of VJN, alongside Education, Peace Building, and Sports, Culture & Arts. Recognizing that many youth come from vulnerable families, orphans, and unemployed backgrounds, VJN established this program to enhance their capabilities.'),
    t('programs.economic.details.initial', 'The program started by supporting 120 orphans and vulnerable children through FHI 360, providing school fees, materials, food, and housing. These families were organized into six savings and credit groups in Rubavu District, Gisenyi, Rugerero, and Nyamyumba sectors.'),
    t('programs.economic.details.expansion', 'VJN has helped youth become self-employed through USAID-funded training in the HUGUKA DUKORE AKAZI KANOZE project. The organization also supports young mothers and their parents through savings and credit groups with MISEREOR and IMBUTO FOUNDATION.'),
    t('programs.economic.details.current', 'Currently, VJN has over 200 savings and credit groups across the country, expanding from the initial three sectors in Rubavu District.')
  ];

  const objectives = [
    t('programs.economic.objectives.main', 'To advance partners and provide youth with knowledge to help them find paid employment or become self-employed, aiming to reduce youth unemployment.'),
    t('programs.economic.objectives.specific1', 'Train youth with knowledge to help them face life challenges, find employment, or start profitable businesses.'),
    t('programs.economic.objectives.specific2', 'Enhance facilitators\' knowledge to help organize youth into savings and credit groups, monitor them, and help them discover opportunities that can generate income.'),
    t('programs.economic.objectives.specific3', 'Encourage and facilitate youth to organize into savings and credit groups using technology to prevent conflicts among them.'),
    t('programs.economic.objectives.specific4', 'Help groups that have completed the savings phase to distribute their assets.'),
    t('programs.economic.objectives.specific5', 'Bring together implementing agencies and youth, especially the unemployed, to help them find paid employment and improve their interaction with VJN.'),
    t('programs.economic.objectives.specific6', 'Support youth with innovative and profitable projects through competitions.'),
    t('programs.economic.objectives.specific7', 'Encourage youth to participate in government programs.'),
    t('programs.economic.objectives.specific8', 'Visit and monitor partners\' activities to support their sustainability.'),
    t('programs.economic.objectives.specific9', 'Establish exemplary income-generating activities within VJN for its learners.')
  ];

  const targetGroups = {
    direct: [
      t('programs.economic.target.direct.facilitators', 'Facilitators'),
      t('programs.economic.target.direct.youth', 'Youth'),
      t('programs.economic.target.direct.adults', 'Adults (both genders)'),
      t('programs.economic.target.direct.employed', 'Employed and unemployed'),
      t('programs.economic.target.direct.disabled', 'People with and without disabilities')
    ],
    indirect: [
      t('programs.economic.target.indirect.authorities', 'Government authorities'),
      t('programs.economic.target.indirect.financial', 'Financial institutions'),
      t('programs.economic.target.indirect.ngos', 'NGOs'),
      t('programs.economic.target.indirect.private', 'Private sector')
    ]
  };

  const services = [
    {
      title: t('programs.economic.services.training.title', 'Training Programs'),
      items: [
        { name: 'WRN (Akazi Kanoze)', duration: '20 days' },
        { name: 'BYOB (Kora Wikorera)', duration: '15 days' },
        { name: 'GSFW (Tegura umurimo)', duration: '15 days' },
        { name: 'SYB (Gutangira igikorwa)', duration: '9 days' },
        { name: 'IYB (Kunoza igikorwa)', duration: '9 days' },
        { name: 'Life Skills', duration: '9 days' },
        { name: 'I am Entrepreneur', duration: '9 days' },
        { name: 'Digital Skills', duration: '9 days' },
        { name: 'Business Recovery', duration: '15 days' }
      ]
    },
    {
      title: t('programs.economic.services.support.title', 'Support Services'),
      items: [
        { name: t('programs.economic.services.support.meetings', 'Youth-Entrepreneur Meetings'), duration: '2 times per year' },
        { name: t('programs.economic.services.support.funding', 'Project Funding'), duration: '1 time' },
        { name: t('programs.economic.services.support.monitoring', 'Activity Monitoring'), duration: 'Throughout agreement' }
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="relative mb-16 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-green-800/90 z-10" />
        <div className="relative z-20 p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-green-600/30 rounded-full mb-6">
              <TrendingUp className="h-8 w-8" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              {t('programs.economic.title', 'Economic Empowerment Program')}
            </h1>
            <p className="text-xl mb-8 text-green-100 leading-relaxed">
              {t('programs.economic.intro.description', 'Our Economic Empowerment Program equips youth with the skills and knowledge needed to become self-employed or find meaningful employment. Through comprehensive training, mentorship, and support services, we help young people build sustainable livelihoods and contribute to economic development.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Users className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">200+</div>
                  <div className="text-sm text-green-100">Savings Groups</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Globe className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">120</div>
                  <div className="text-sm text-green-100">Initial Beneficiaries</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Heart className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">9+</div>
                  <div className="text-sm text-green-100">Training Programs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="md:w-1/3">
            <div className="bg-white p-8 rounded-xl shadow-md border border-green-100">
              <div className="text-green-600 mb-6">
                <TrendingUp size={48} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {t('programs.economic.title', 'Economic Empowerment Program')}
              </h2>
              <h3 className="text-xl font-semibold text-green-600 mb-4">
                {t('programs.economic.subtitle', 'Building Sustainable Livelihoods')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('programs.economic.description', 'Our program addresses youth unemployment by providing comprehensive training, mentorship, and support services to help young people become self-employed or find meaningful employment.')}
              </p>
            </div>
          </div>
          
          <div className="md:w-2/3">
            <div className="bg-white rounded-xl shadow-md p-8 border border-green-100">
              <h4 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('programs.economic.details.title', 'Program Details')}
              </h4>
              <div className="space-y-6">
                {economicDetails.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-colors">
                      <span className="text-green-600 font-semibold group-hover:text-white">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Target className="h-8 w-8 mr-3 text-green-600" />
          {t('programs.economic.objectives.title', 'Program Objectives')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-green-100 group">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                <Award className="h-6 w-6 text-green-600 group-hover:text-white" />
              </div>
              <p className="text-gray-700 leading-relaxed">{objective}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Users className="h-8 w-8 mr-3 text-green-600" />
          {t('programs.economic.target.title', 'Target Groups')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl p-8 shadow-md border border-green-100">
            <h3 className="text-2xl font-semibold text-green-600 mb-6">
              {t('programs.economic.target.direct.title', 'Direct Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.direct.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                    <Clock className="h-5 w-5 text-green-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-md border border-green-100">
            <h3 className="text-2xl font-semibold text-green-600 mb-6">
              {t('programs.economic.target.indirect.title', 'Indirect Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.indirect.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                    <Clock className="h-5 w-5 text-green-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Shield className="h-8 w-8 mr-3 text-green-600" />
          {t('programs.economic.services.title', 'Our Services')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-md border border-green-100">
              <h3 className="text-2xl font-semibold text-green-600 mb-6">{service.title}</h3>
              <div className="space-y-4">
                {service.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center justify-between p-4 bg-green-50 rounded-lg group hover:bg-green-100 transition-colors">
                    <span className="text-gray-700 font-medium">{item.name}</span>
                    <span className="text-green-600 text-sm font-medium">{item.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl shadow-xl p-12">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6 text-white">
            {t('programs.economic.cta.title', 'Join Our Economic Empowerment Program')}
          </h2>
          <p className="text-xl text-green-100 mb-10 leading-relaxed">
            {t('programs.economic.cta.description', 'Be part of our mission to reduce youth unemployment and build sustainable livelihoods. Your involvement can make a difference in the lives of young people.')}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/volunteer">
              <Button className="bg-white text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.economic.cta.volunteer', 'Volunteer With Us')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-green-700 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.economic.cta.donate', 'Support Our Programs')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Economic;
